import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';
import { O as Oe } from './index.esm-CXYreJ4v.js';
import { P as Pubsub } from './pubsub-DKqIEqaC.js';
import './notyf-DIw0RvBj.js';
import { e as emblaScrollbar } from './scrollbar-CKxfKmNg.js';

const WHEEL_MIN_PX = 20;
// Short debounce so one physical click can't fire multiple advances, while
// still allowing every deliberate tick to advance a slide (250ms << 1 tick interval).
const WHEEL_DEBOUNCE_MS = 250;

class ProductMediaSlider extends HTMLElement {
  #viewport = this.querySelector('.embla__viewport');
  #scrollbarThumb = this.querySelector('.embla__scrollbar__thumb');
  /** @type {import('embla-carousel').EmblaCarouselType} */ #embla;
  #wheelTimer = null;
  #desktop = window.matchMedia('(min-width: 1024px)');
  #unsubscribeThumbnailsReady;

  connectedCallback() {
    this.#unsubscribeThumbnailsReady = Pubsub.subscribe(
      'product-media-thumbnails:ready',
      this.#onThumbnailsReady
    );
    this.#initCarousel();
    this.#connectThumbnails();
    this.#initFancybox();
  }

  get embla() {
    return this.#embla;
  }

  disconnectedCallback() {
    clearTimeout(this.#wheelTimer);
    this.#viewport?.removeEventListener('wheel', this.#onWheel);
    this.#unsubscribeThumbnailsReady?.();
    this.#embla?.destroy();
  }

  #initCarousel() {
    if (!this.#viewport) return;

    this.#embla = EmblaCarousel(this.#viewport, {
      loop: true,
      skipSnaps: true,
      breakpoints: {
        // duration >= 20 keeps embla's scroll body critically damped. Below that it
        // overshoots the snap and visibly bounces back, even from a standstill —
        // at 15 that was ~9.5px on a full-height slide. 21 is the snappiest value
        // with zero overshoot (the default is 25).
        '(min-width: 1024px)': { axis: 'y', loop: false, duration: 21, skipSnaps: false },
      },
    });

    this.#viewport.addEventListener('wheel', this.#onWheel, { passive: false });

    emblaScrollbar(this.#embla, this.#scrollbarThumb);
  }

  #connectThumbnails() {
    const thumbnails = this.querySelector('product-media-thumbnails');
    thumbnails?.connect?.(this.#embla);
  }

  #onThumbnailsReady = ({ id, data }) => {
    if (id !== this.#viewport?.id) return;

    data.thumbnail.connect(this.#embla);
  };

  /**
   * Desktop-only. Each discrete wheel tick advances one slide immediately,
   * without waiting for the current animation to settle. Embla handles
   * overlapping animations natively. A short debounce prevents sub-events
   * within a single physical click from firing twice.
   * Releases to the page at the first/last slide (releaseOnEdges).
   * @param {WheelEvent} event
   */
  #onWheel = (event) => {
    if (!this.#desktop.matches) return;

    const delta = this.#wheelToPixels(event);
    if (delta === 0) return;

    const forward = delta > 0;

    // Release to page scroll when exhausted in this direction.
    if (forward ? !this.#embla.canGoToNext() : !this.#embla.canGoToPrev()) return;

    // Carousel owns this direction — prevent page scroll for all events,
    // including sub-threshold ones that won't advance a slide.
    event.preventDefault();

    if (Math.abs(delta) < WHEEL_MIN_PX) return;

    if (this.#wheelTimer !== null) return;
    this.#wheelTimer = setTimeout(() => {
      this.#wheelTimer = null;
    }, WHEEL_DEBOUNCE_MS);

    if (forward) this.#embla.goToNext();
    else this.#embla.goToPrev();
  };

  /**
   * Wheel delta in pixels — Firefox reports it in lines (deltaMode 1).
   * @param {WheelEvent} event
   * @returns {number}
   */
  #wheelToPixels(event) {
    return event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
  }

  #initFancybox() {
    const { chevronLeft, chevronRight, xMark, plus, minus, arrowTurn } = window.icons.regular;

    Oe.bind('[data-fancybox]', {
      idle: false,
      contentClick: 'toggleCover',
      wheel: 'slide',

      // Toolbar
      Toolbar: {
        absolute: true,
        display: {
          left: ['prev', 'close'],
          middle: ['zoomInCustom', 'zoomOutCustom', 'resetCustom'],
          right: ['next'],
        },

        items: {
          prev: {
            tpl: `<button class="f-button" title="{{PREV}}" data-fancybox-prev>${chevronLeft}</button>`,
          },
          next: {
            tpl: `<button class="f-button" title="{{NEXT}}" data-fancybox-next>${chevronRight}</button>`,
          },
          close: {
            tpl: `<button class="f-button" title="{{CLOSE}}" data-fancybox-close>${xMark}</button>`,
          },

          // The following items are not default Fancybox items, so we have to add them custom
          zoomInCustom: {
            tpl: `<button class="f-button" title="Zoom In" data-panzoom-action="zoomIn">${plus}</button>`,
          },
          zoomOutCustom: {
            tpl: `<button class="f-button" title="Zoom Out" data-panzoom-action="zoomOut">${minus}</button>`,
          },
          resetCustom: {
            tpl: `<button class="f-button" title="Reset" data-panzoom-action="reset">${arrowTurn}</button>`,
          },
        },
      },

      // Thumbs
      Thumbs: {
        type: 'classic',
        Carousel: {
          enabled: false,
          breakpoints: {
            '(min-width: 768px)': {
              enabled: true,
              axis: 'y',
              slidesPerPage: 1,
              center: true,
              fill: true,
              dragFree: true,
              Navigation: {
                prevTpl: chevronLeft,
                nextTpl: chevronRight,
              },
            },
          },
        },
      },

      // Images
      Images: {
        Panzoom: {
          maxScale: 5,
          step: 1.2,
        },
      },

      Html: {
        videoTpl: `<video class="fancybox__html5video" loop muted playsinline controls controlsList="nodownload" poster="{{poster}}"><source src="{{src}}" type="{{format}}" />Sorry, your browser doesn't support embedded videos.</video>`,
      },

      // Carousel
      Carousel: {
        infinite: true,
        Navigation: false,
        Panzoom: {
          touch: (event) => event?.target.nodeName !== 'MODEL-VIEWER',
        },
      },

      on: {
        close: this.#onLightboxClose,
      },
    });
  }

  /**
   * On close, Fancybox focuses the trigger of the last-viewed slide
   * (placeFocusBack), which makes the browser natively scroll the
   * overflow-hidden viewport to reveal it. Embla only tracks its own
   * transform, so that native scroll leaves the carousel desynced: the wheel
   * handler keeps preventing page scroll based on stale state. Fancybox
   * focuses the trigger before emitting `close`, so undoing the native scroll
   * here is safe — and Embla jumps to the viewed slide to keep it in view.
   * @param {import('@fancyapps/ui').Fancybox} fancybox
   */
  #onLightboxClose = (fancybox) => {
    const slide = fancybox.getSlide();
    if (!this.#embla || !slide?.triggerEl || !this.contains(slide.triggerEl)) return;

    this.#viewport.scrollTop = 0;
    this.#viewport.scrollLeft = 0;
    this.#embla.goTo(slide.index, true);
  };
}

customElements.define('product-media-slider', ProductMediaSlider);
